import { Typography } from '@material-ui/core';
import React, { CSSProperties, FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { WarningIcon } from '../../constants/icon-definitions';
import { colorDef } from '../../constants/styles/color-def';
import { LoadingButtonState } from './mui/loading-button-state';
import { ButtonWithLoadingCircle } from './mui/mui-loading-button';

const Root = styled.div`
  padding: 10px;
  background-color: hsl(0, 0%, 95%);
  display: flex;
`;

const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
`;

const RightSide = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`;

interface Props {
  messages: readonly string[];
  status?: 'default' | 'warning' | 'alert';
  showButton?: boolean;
  buttonLabel?: string;
  buttonIsDisabled?: boolean;
  buttonStatus?: LoadingButtonState['status'];
  onButtonClick?: () => void;
}

export const MessageArea: FC<Props> = memo(
  ({
    messages,
    status = 'default',
    showButton = false,
    buttonLabel = '',
    buttonIsDisabled = false,
    buttonStatus = 'init',
    onButtonClick = () => undefined
  }: Readonly<Props>) => {
    const bgColor = useMemo<CSSProperties>(() => {
      switch (status) {
        case 'default':
          return {
            backgroundColor: colorDef.messageArea.default
          };
        case 'warning':
          return {
            backgroundColor: colorDef.messageArea.warning
          };
        case 'alert':
          return {
            backgroundColor: colorDef.messageArea.alert
          };
      }
    }, [status]);

    return (
      <Root style={bgColor}>
        <LeftSide>
          {status !== 'default' ? (
            <Alert>
              <WarningIcon />
            </Alert>
          ) : (
            undefined
          )}
          {messages.map((msg, idx) => (
            <Typography key={idx}>{msg}</Typography>
          ))}
        </LeftSide>
        <RightSide>
          {showButton ? (
            <ButtonWithLoadingCircle
              status={buttonStatus}
              label={buttonLabel}
              disabled={buttonIsDisabled}
              onClick={onButtonClick}
            />
          ) : (
            undefined
          )}
        </RightSide>
      </Root>
    );
  }
);

MessageArea.displayName = 'MessageArea';
